import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StreamEmbed } from "@/components/Stream";

const MATCH_STATUS_LABEL = { pending: "PENDING", live: "LIVE NOW", finished: "FINAL" };

export default function TestBracket() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const load = () => api.get(`/tournaments/${slug}/bracket`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [slug]);

    const t = data?.tournament;
    const standings = data?.standings || [];
    const matches = data?.matches || [];
    const teamCount = data?.team_count || 0;
    const maxTeams = data?.max_teams || 16;
    const filledSlots = useMemo(() => Array.from({ length: maxTeams }).map((_, i) => standings[i] || null), [standings, maxTeams]);
    const alreadyIn = user && standings.some(s => (s.roster || []).some(r => r.user_id === user.id));

    // Signup form state
    const [form, setForm] = useState({ team_name: "", tag: "", teammate1_gamer_tag: "", teammate2_gamer_tag: "" });
    const canSignup = user && !alreadyIn && teamCount < maxTeams && t?.status !== "finished";

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!user) { toast.error("Sign in first to enter"); return; }
        setSubmitting(true);
        try {
            await api.post(`/tournaments/${slug}/test-signup`, form);
            toast.success("You're in — trio locked into the bracket");
            setForm({ team_name: "", tag: "", teammate1_gamer_tag: "", teammate2_gamer_tag: "" });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Signup failed");
        } finally { setSubmitting(false); }
    };

    if (loading) return <div className="pt-40 text-center text-white/40 font-mono text-xs tracking-[0.3em]">LOADING BRACKET…</div>;
    if (!t) return <div className="pt-40 text-center text-white/60 font-display text-3xl">Bracket not found.</div>;

    return (
        <div className="pt-[68px]" data-testid="test-bracket-page">
            {/* HERO */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-20 pb-10">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="overline text-volt">[ Test Bracket · Open Entry ]</span>
                    <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-signal">
                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" /> Live sandbox
                    </span>
                </div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-[7vw]" data-testid="bracket-title">
                    {t.name}
                </h1>
                <p className="mt-6 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">{t.description}</p>

                {/* ELO REWARDS */}
                <div className="mt-10" data-testid="elo-rewards">
                    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                        <div className="overline">[ ELO Rewards · Per Match ]</div>
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">
                            Earnable across all 3 matches
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <EloRewardCard place="1st" elo={1000} accent testId="elo-reward-1st" />
                        <EloRewardCard place="2nd" elo={750} testId="elo-reward-2nd" />
                        <EloRewardCard place="3rd" elo={500} testId="elo-reward-3rd" />
                        <EloRewardCard place="4th" elo={250} testId="elo-reward-4th" />
                    </div>
                    <div className="mt-4 border border-volt/40 bg-volt/[0.06] clip-frame-sm px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="elo-tournament-bonus">
                        <div>
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-volt mb-1">Tournament Winner Bonus</div>
                            <div className="font-display font-black text-lg md:text-xl leading-tight">Highest total points after all 3 matches</div>
                        </div>
                        <div className="font-display font-black text-3xl md:text-4xl text-volt tracking-tight">+1,500 <span className="text-sm md:text-base tracking-[0.28em] font-mono text-volt/80">ELO</span></div>
                    </div>
                </div>

                {/* MAP POOL */}
                <div className="mt-10" data-testid="map-pool">
                    <div className="overline mb-4">[ Map Pool · 3 Drops ]</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <MapCard
                            name="Rebirth Island"
                            image="https://customer-assets-lqy194kg.emergentagent.net/job_ranked-play/artifacts/800xenm5_RebirthIsland_LoadingScreen_Season4Reloaded_BO7_WZ2.webp"
                            testId="map-rebirth"
                        />
                        <MapCard
                            name="Fortune's Keep"
                            image="https://customer-assets-lqy194kg.emergentagent.net/job_ranked-play/artifacts/rs0iarld_WZ-MOF-Fortune-Keep-New-Caldera-Guide-009.jpg"
                            testId="map-keep"
                        />
                        <MapCard
                            name="Heaven's Hollow"
                            image="https://customer-assets-lqy194kg.emergentagent.net/job_ranked-play/artifacts/c9c48pe8_hhwz.jpeg"
                            testId="map-heavens-hollow"
                        />
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
                    <StatCell label="Format" value={t.format || "WSOW · 3 Matches"} accent />
                    <StatCell label="Teams" value={`${teamCount} / ${maxTeams}`} testId="bracket-teams-stat" />
                    <StatCell label="Team size" value={`${t.team_size} · Trios`} />
                    <StatCell label="Status" value={t.status.toUpperCase()} />
                </div>
            </section>

            {/* STREAM + SIGNUP */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                    <StreamEmbed platform={t.stream_platform} channel={t.stream_channel} url={t.stream_url} />
                </div>
                <div className="lg:col-span-4">
                    <div className="border border-white/10 clip-frame p-6 bg-[#0A0A0A] h-full" data-testid="signup-card">
                        <div className="overline mb-4">[ Sign a Trio ]</div>
                        {!user && (
                            <div className="text-sm text-white/70 leading-relaxed">
                                <p>Sign in to drop a trio into the sandbox. <span className="text-white/50">No state ID needed for test brackets.</span></p>
                                <Link to="/login" className="btn-primary mt-6 inline-block" data-testid="signup-signin-cta">Sign in →</Link>
                            </div>
                        )}
                        {user && alreadyIn && (
                            <div className="text-sm text-white/70">
                                <p className="text-volt font-mono text-[10px] tracking-[0.28em] uppercase mb-3">You're locked in ✓</p>
                                <p>Your trio is on the roster. Watch the standings below.</p>
                            </div>
                        )}
                        {user && !alreadyIn && teamCount >= maxTeams && (
                            <div className="text-sm text-white/70">
                                <p className="text-signal font-mono text-[10px] tracking-[0.28em] uppercase mb-3">Bracket full</p>
                                <p>All 16 trios locked in — check standings below.</p>
                            </div>
                        )}
                        {canSignup && (
                            <form onSubmit={handleSignup} className="space-y-3" data-testid="signup-form">
                                <TextField label="Team name" value={form.team_name} onChange={v => setForm({ ...form, team_name: v })} testId="signup-team-name" required />
                                <TextField label="Tag (2–6)" value={form.tag} onChange={v => setForm({ ...form, tag: v.toUpperCase() })} testId="signup-tag" maxLength={6} required />
                                <TextField label="Teammate 1 gamer tag" value={form.teammate1_gamer_tag} onChange={v => setForm({ ...form, teammate1_gamer_tag: v })} testId="signup-teammate1" required />
                                <TextField label="Teammate 2 gamer tag" value={form.teammate2_gamer_tag} onChange={v => setForm({ ...form, teammate2_gamer_tag: v })} testId="signup-teammate2" required />
                                <button type="submit" disabled={submitting} className="btn-primary w-full mt-2 disabled:opacity-50" data-testid="signup-submit">
                                    {submitting ? "Locking in…" : "Enter the bracket →"}
                                </button>
                                <p className="text-[10px] font-mono text-white/40 pt-2">Captain = you ({user.gamer_tag}). No ID gate for test brackets.</p>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* ROSTER GRID */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="overline mb-2">[ Roster · 16 Trios ]</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl">The Field.</h2>
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40">{teamCount}/{maxTeams} filled</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="roster-grid">
                    {filledSlots.map((slot, i) => (
                        <div key={i} className={`border p-4 clip-frame-sm ${slot ? "border-volt/40 bg-volt/[0.04]" : "border-white/8 bg-[#080808]"}`} data-testid={slot ? `roster-slot-filled-${i}` : `roster-slot-empty-${i}`}>
                            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">SEAT {String(i + 1).padStart(2, "0")}</div>
                            {slot ? (
                                <>
                                    <div className="font-display font-extrabold text-base leading-tight truncate">{slot.team_name}</div>
                                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] text-volt">[{slot.team_tag}]</div>
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {(slot.roster || []).map((p, idx) => (
                                            <span key={idx} className={`text-[10px] font-mono px-2 py-0.5 border ${p.role === "captain" ? "border-volt/60 text-volt" : "border-white/15 text-white/60"}`}>
                                                {p.gamer_tag}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-white/25 font-display text-lg">Open</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* MATCHES */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16" data-testid="matches-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="overline mb-2">[ Match Log · WSOW ]</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl">Three drops.</h2>
                    </div>
                    <div className="hidden md:block font-mono text-[10px] tracking-[0.24em] uppercase text-white/45 text-right">
                        Placement · 1st=10, 2nd=6, 3rd=5, 4th=4, 5th=3, 6th=2, 7-10=1<br />
                        + 1 pt per kill
                    </div>
                </div>
                <div className="space-y-4">
                    {matches.map((m) => (
                        <MatchCard key={m.id} match={m} teams={standings} isAdmin={user?.role === "admin"} slug={slug} onUpdated={load} />
                    ))}
                </div>
            </section>

            {/* STANDINGS */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32" data-testid="standings-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="overline mb-2">[ Overall Standings ]</div>
                        <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl">The Verdict.</h2>
                    </div>
                </div>
                {standings.length === 0 ? (
                    <div className="border border-white/10 p-16 text-center text-white/40 font-display text-2xl">No teams yet — be the first trio in.</div>
                ) : (
                    <div className="border border-white/10 clip-frame overflow-hidden">
                        <table className="w-full">
                            <thead className="bg-white/[0.03] border-b border-white/10">
                                <tr className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45">
                                    <th className="text-left px-4 py-3 w-16">Rank</th>
                                    <th className="text-left px-4 py-3">Trio</th>
                                    <th className="text-left px-4 py-3 hidden md:table-cell">Roster</th>
                                    <th className="text-right px-4 py-3">Matches</th>
                                    <th className="text-right px-4 py-3">Points</th>
                                </tr>
                            </thead>
                            <tbody>
                                {standings.map((s) => (
                                    <tr key={s.team_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`standings-row-${s.rank}`}>
                                        <td className="px-4 py-3 font-display font-black text-xl">{s.rank}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-display font-extrabold">{s.team_name}</div>
                                            <div className="font-mono text-[10px] tracking-[0.22em] text-volt mt-0.5">[{s.team_tag}]</div>
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <div className="flex flex-wrap gap-1.5">
                                                {(s.roster || []).map((p, idx) => (
                                                    <span key={idx} className={`text-[10px] font-mono px-2 py-0.5 border ${p.role === "captain" ? "border-volt/60 text-volt" : "border-white/15 text-white/60"}`}>{p.gamer_tag}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-sm text-white/70">{s.matches_played}</td>
                                        <td className="px-4 py-3 text-right font-display font-black text-xl text-volt">{s.points}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

const StatCell = ({ label, value, accent = false, testId }) => (
    <div className="bg-[#050505] px-5 py-6" data-testid={testId}>
        <div className="overline text-white/40 mb-2">{label}</div>
        <div className={`font-display font-black text-xl md:text-2xl ${accent ? "text-volt" : "text-white"} truncate`}>{value}</div>
    </div>
);

const EloRewardCard = ({ place, elo, accent = false, testId }) => (
    <div
        className={`border clip-frame-sm px-5 py-5 bg-[#080808] ${accent ? "border-volt/60 shadow-[0_0_0_1px_rgba(204,255,0,0.15)]" : "border-white/10"}`}
        data-testid={testId}
    >
        <div className="flex items-baseline justify-between mb-3">
            <div className={`font-display font-black text-3xl md:text-4xl leading-none ${accent ? "text-volt" : "text-white"}`}>{place}</div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">Place</div>
        </div>
        <div className={`font-display font-black text-2xl md:text-3xl tracking-tight ${accent ? "text-volt" : "text-white/90"}`}>
            +{elo.toLocaleString()}
        </div>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45 mt-1">ELO per match</div>
    </div>
);

const MapCard = ({ name, image, testId }) => (
    <div className="group relative overflow-hidden border border-white/10 clip-frame-sm bg-[#080808]" data-testid={testId}>
        <div className="aspect-[16/10] overflow-hidden">
            <img
                src={image}
                alt={name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
        </div>
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 flex items-end justify-between">
            <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-volt mb-1">Drop</div>
                <div className="font-display font-black text-xl md:text-2xl leading-tight">{name}</div>
            </div>
            <div className="w-2 h-2 rounded-full bg-volt shadow-[0_0_10px_2px_rgba(204,255,0,0.5)]" />
        </div>
    </div>
);

const TextField = ({ label, value, onChange, testId, required, maxLength }) => (
    <label className="block">
        <span className="overline text-white/50">{label}</span>
        <input
            type="text" required={required} value={value} maxLength={maxLength}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 w-full bg-void border border-white/15 focus:border-volt/60 outline-none px-3 py-2.5 font-mono text-sm text-white transition-colors"
        />
    </label>
);

const MatchCard = ({ match, teams, isAdmin, slug, onUpdated }) => {
    const [showAdmin, setShowAdmin] = useState(false);
    const [rows, setRows] = useState(() => (match.results || []).map(r => ({ team_id: r.team_id, placement: r.placement, kills: r.kills })));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setRows((match.results || []).map(r => ({ team_id: r.team_id, placement: r.placement, kills: r.kills })));
    }, [match.id, match.results]);

    const addRow = () => setRows([...rows, { team_id: teams[0]?.team_id || "", placement: rows.length + 1, kills: 0 }]);
    const updateRow = (i, key, val) => setRows(rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    const submit = async () => {
        setSaving(true);
        try {
            const payload = { results: rows.map(r => ({ team_id: r.team_id, placement: Number(r.placement), kills: Number(r.kills) })) };
            await api.post(`/admin/tournaments/${slug}/matches/${match.match_number}/report`, payload);
            toast.success(`Match ${match.match_number} results locked in`);
            setShowAdmin(false);
            onUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save match");
        } finally { setSaving(false); }
    };

    const startMatch = async () => {
        try {
            await api.post(`/admin/tournaments/${slug}/matches/${match.match_number}/start`);
            toast.success(`Match ${match.match_number} is live`);
            onUpdated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to start match");
        }
    };

    const statusStyle = { pending: "text-white/45 border-white/10", live: "text-signal border-signal/40", finished: "text-volt border-volt/40" }[match.status] || "";

    return (
        <div className="border border-white/10 clip-frame bg-[#0A0A0A]" data-testid={`match-card-${match.match_number}`}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div className="flex items-center gap-4">
                    <div className="font-display font-black text-2xl md:text-3xl text-volt">M{match.match_number}</div>
                    <div>
                        <div className="font-display font-extrabold text-base md:text-lg">{match.map || `Match ${match.match_number}`}</div>
                        <div className={`font-mono text-[10px] tracking-[0.28em] uppercase border px-2 py-0.5 inline-block mt-1 ${statusStyle}`}>{MATCH_STATUS_LABEL[match.status] || match.status.toUpperCase()}</div>
                    </div>
                </div>
                {isAdmin && (
                    <div className="flex gap-2">
                        {match.status === "pending" && (
                            <button onClick={startMatch} className="btn-ghost !py-2 !px-3 !text-[10px]" data-testid={`start-match-${match.match_number}`}>Start</button>
                        )}
                        <button onClick={() => setShowAdmin(!showAdmin)} className="btn-ghost !py-2 !px-3 !text-[10px]" data-testid={`admin-report-toggle-${match.match_number}`}>
                            {showAdmin ? "Close" : (match.results?.length ? "Edit results" : "Report results")}
                        </button>
                    </div>
                )}
            </div>

            {/* Results */}
            {match.results && match.results.length > 0 && (
                <div className="px-5 py-4">
                    <div className="overline text-white/40 mb-3">[ Results ]</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                        {match.results.slice(0, 16).map((r) => (
                            <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-white/5 text-sm">
                                <div className="flex items-center gap-3">
                                    <span className="font-display font-black text-lg w-8 text-white/60">{r.placement}</span>
                                    <span className="font-display font-extrabold">{r.team_name}</span>
                                    <span className="font-mono text-[10px] text-volt tracking-[0.2em]">[{r.team_tag}]</span>
                                </div>
                                <div className="flex items-center gap-4 font-mono text-xs text-white/60">
                                    <span>{r.kills} K</span>
                                    <span className="text-volt font-display font-black text-base">{r.total_pts} pts</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {(!match.results || match.results.length === 0) && !showAdmin && (
                <div className="px-5 py-8 text-center text-white/35 font-mono text-xs tracking-[0.28em] uppercase">Awaiting results</div>
            )}

            {/* Admin input */}
            {isAdmin && showAdmin && (
                <div className="px-5 py-5 border-t border-white/10 bg-[#050505]" data-testid={`admin-report-form-${match.match_number}`}>
                    <div className="overline text-white/50 mb-4">Enter placement + kills per trio</div>
                    <div className="space-y-2">
                        {rows.map((r, i) => (
                            <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                <select value={r.team_id} onChange={(e) => updateRow(i, "team_id", e.target.value)} className="col-span-6 bg-void border border-white/15 px-3 py-2 font-mono text-xs">
                                    <option value="">— pick trio —</option>
                                    {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name} [{t.team_tag}]</option>)}
                                </select>
                                <input type="number" min="1" max="16" value={r.placement} onChange={(e) => updateRow(i, "placement", e.target.value)} placeholder="place" className="col-span-2 bg-void border border-white/15 px-2 py-2 font-mono text-xs text-center" />
                                <input type="number" min="0" max="60" value={r.kills} onChange={(e) => updateRow(i, "kills", e.target.value)} placeholder="kills" className="col-span-3 bg-void border border-white/15 px-2 py-2 font-mono text-xs text-center" />
                                <button onClick={() => removeRow(i)} className="col-span-1 text-white/40 hover:text-signal text-lg">×</button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex gap-3">
                        <button onClick={addRow} className="btn-ghost !py-2 !px-3 !text-[10px]" data-testid={`admin-add-row-${match.match_number}`}>+ Add trio</button>
                        <button onClick={submit} disabled={saving || rows.length === 0} className="btn-primary !py-2 !px-4 !text-[10px] disabled:opacity-40" data-testid={`admin-save-${match.match_number}`}>
                            {saving ? "Saving…" : "Lock results →"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
